import { useState } from 'react';
import { Avatar, Button, useConfirm } from '@/components';
import { formatDate } from '@/lib/format';
import type { WorkspaceUser } from '@shared/types';

export interface UsersPanelProps {
  users: WorkspaceUser[];
  /** Marks the viewer's own row, and gates the confirm on demoting yourself. */
  currentUserId: string;
  /** Resolves true when the invite was sent, so the form knows it can clear. */
  onInvite: (email: string, role: 'member' | 'admin') => Promise<boolean>;
  onChangeRole: (userId: string, role: 'member' | 'admin') => void;
  onRemove: (userId: string) => void;
}

/** Nothing records "invited but not accepted"; it is read off the two dates. */
function accessNote(user: WorkspaceUser): string {
  if (user.lastSignInAt) return `last signed in ${formatDate(user.lastSignInAt)}`;
  if (user.invitedAt) return `invited ${formatDate(user.invitedAt)} · not accepted yet`;
  return 'never signed in';
}

/**
 * Administrators only — the API behind it refuses anyone else, so the tab that
 * leads here is not offered to a member.
 */
export const UsersPanel: React.FC<UsersPanelProps> = ({
  users,
  currentUserId,
  onInvite,
  onChangeRole,
  onRemove,
}) => {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [inviting, setInviting] = useState(false);
  const confirm = useConfirm();

  // The server refuses to leave the workspace without an administrator; the
  // select is locked here too so the only one is not offered a change that
  // would only come back as an error.
  const adminCount = users.filter((u) => u.role === 'admin').length;

  async function changeRole(user: WorkspaceUser, next: 'member' | 'admin') {
    if (next === user.role) return;
    // Demoting yourself removes this whole tab, which is worth a beat's pause.
    if (user.id === currentUserId && next === 'member') {
      const ok = await confirm({
        title: 'Make yourself a member?',
        body: 'You will lose access to budgets, the Locate2u connection and this page. Another administrator would have to restore it.',
        confirmLabel: 'Make me a member',
      });
      if (!ok) return;
    }
    onChangeRole(user.id, next);
  }

  async function remove(user: WorkspaceUser) {
    const ok = await confirm({
      title: `Remove ${user.email}?`,
      body: 'Their access ends immediately. Agents, knowledge and chat history they created stay in the workspace. This cannot be undone — they would need a fresh invite.',
      confirmLabel: 'Remove user',
    });
    if (ok) onRemove(user.id);
  }

  async function submit() {
    const address = email.trim();
    if (!address) return;
    setInviting(true);
    const sent = await onInvite(address, role);
    setInviting(false);
    // A rejected address stays in the field so it can be corrected.
    if (sent) {
      setEmail('');
      setRole('member');
    }
  }

  return (
    <>
      <div className="settings__card settings__card--wide">
        <div className="field-label">Invite someone</div>
        <p className="settings__hint">
          They get an email with a link to set their own password. Nobody else ever sees it.
        </p>

        <div className="settings__invite-row">
          <input
            className="text-input"
            type="email"
            value={email}
            placeholder="name@company.com"
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
          />
          <select
            className="select-input settings__invite-role"
            value={role}
            onChange={(e) => setRole(e.target.value as 'member' | 'admin')}
          >
            <option value="member">Member</option>
            <option value="admin">Admin</option>
          </select>
          <Button size="sm" loading={inviting} disabled={!email.trim()} onClick={() => void submit()}>
            Send invite
          </Button>
        </div>

        <p className="settings__hint settings__hint--foot">
          Members can use agents, edit them and manage knowledge. Admins can also change budgets,
          the default model, the Locate2u connection, and invite people.
        </p>
      </div>

      <div className="settings__card settings__card--wide">
        <div className="settings__table-head">
          <div className="field-label">People with access</div>
          <div className="settings__table-total">
            {users.length} {users.length === 1 ? 'person' : 'people'}
          </div>
        </div>

        <div className="users-table">
          <div className="users-table__head">
            <span>User</span>
            <span>Role</span>
            <span>Access</span>
            <span className="users-table__right">Added</span>
            <span />
          </div>
          {users.map((user) => {
            const lastAdmin = user.role === 'admin' && adminCount === 1;
            const isSelf = user.id === currentUserId;
            return (
              <div key={user.id} className="users-table__row">
                <span className="users-table__user">
                  <Avatar name={user.displayName || user.email} colour="#2B93FF" size={26} />
                  <span className="users-table__identity">
                    {/* The email carries the row when there is no display name,
                        so it takes the primary line rather than sitting under
                        an empty one. */}
                    <span className="users-table__name">
                      <span className="users-table__name-text">
                        {user.displayName || user.email}
                      </span>
                      {isSelf && <span className="users-table__you">you</span>}
                    </span>
                    {user.displayName && <span className="users-table__email">{user.email}</span>}
                  </span>
                </span>
                <select
                  className="select-input users-table__role-select"
                  value={user.role}
                  disabled={lastAdmin}
                  title={
                    lastAdmin
                      ? 'The workspace needs an administrator. Make someone else one first.'
                      : undefined
                  }
                  onChange={(e) => changeRole(user, e.target.value as 'member' | 'admin')}
                >
                  <option value="member">Member</option>
                  <option value="admin">Admin</option>
                </select>
                <span className="users-table__access">{accessNote(user)}</span>
                <span className="users-table__when users-table__right">
                  {formatDate(user.createdAt)}
                </span>
                {/* Disabled rather than hidden, so the reason can be read off
                    the row instead of the control just being missing. */}
                <button
                  type="button"
                  className="users-table__remove"
                  aria-label={`Remove ${user.email}`}
                  disabled={isSelf || lastAdmin}
                  title={
                    isSelf
                      ? 'You cannot remove your own account.'
                      : lastAdmin
                        ? 'The workspace needs an administrator. Make someone else one first.'
                        : `Remove ${user.email}`
                  }
                  onClick={() => remove(user)}
                >
                  &times;
                </button>
              </div>
            );
          })}
        </div>

        {users.length === 0 && (
          <div className="empty-note">No one else yet — invite a colleague with the form above.</div>
        )}
      </div>
    </>
  );
};

export default UsersPanel;
